import Head from 'next/head';
import { useRouter } from 'next/router';
import * as React from 'react';

import { usePermissions } from '@/hooks/usePermissions';
import type { WebflowExtractedPage } from '@/lib/webflow-poc/extract-content';
import { WEBFLOW_CONTENT_WRAPPER_CLASS } from '@/lib/webflow-poc/extract-content';
import type { WebflowNavigationChrome } from '@/lib/webflow-poc/fetch-public-config';
import { syncGuestAuthLinkVisibility } from '@/lib/webflow-poc/guest-auth-nav-links';
import { injectWebflowScripts } from '@/lib/webflow-poc/inject-webflow-scripts';
import {
  WEBFLOW_CHAKRA_ISOLATION_CSS,
  WEBFLOW_HOVER_ALIGN_CSS,
} from '@/lib/webflow-poc/webflow-isolation';
import { WebflowStaticNavAccountPortal } from '@/lib/webflow-poc/WebflowStaticNavAccountPortal';

type WebflowBodyContentProps = {
  page: WebflowExtractedPage;
  /** When TRADEWING, hide any leftover `#data-tradewing-nav` in the page. */
  navigationChrome?: WebflowNavigationChrome;
};

/** CSS fallback if nav still appears inside content (e.g. nested after extract). */
const WEBFLOW_NAV_HIDE_CSS = `
#data-tradewing-nav,
[data-tradewing-nav],
.data-tradewing-nav {
  display: none !important;
}
`.trim();

const shouldHandleClientNavigation = (
  anchor: HTMLAnchorElement,
  event: MouseEvent
): boolean => {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return false;
  }
  if (anchor.target && anchor.target !== '_self') return false;
  if (anchor.hasAttribute('download')) return false;

  const href = anchor.getAttribute('href');
  if (
    !href ||
    href.startsWith('#') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('javascript:')
  ) {
    return false;
  }

  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return false;
  }

  if (url.origin !== window.location.origin) return false;

  return true;
};

/**
 * Full-page / hybrid Webflow static render.
 *
 * - Isolation keeps Chakra/theme from overriding Webflow CSS (incl. logo sizes).
 * - Same-origin nav links use Next.js client routing (no full browser reload).
 * - jQuery / webflow.js / custom embed scripts are injected after mount so
 *   menus, carousels, and IX2 interactions work.
 */
export const WebflowBodyContent: React.FC<WebflowBodyContentProps> = ({
  page,
  navigationChrome = 'WEBFLOW',
}) => {
  const router = useRouter();
  const permissions = usePermissions();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const hideWebflowNav = navigationChrome === 'TRADEWING';

  const wrapperClassName = [
    WEBFLOW_CONTENT_WRAPPER_CLASS,
    'data-tradewing-content',
    page.bodyClassName,
  ]
    .filter(Boolean)
    .join(' ');

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a');
      if (!(anchor instanceof HTMLAnchorElement) || !root.contains(anchor)) {
        return;
      }

      if (!shouldHandleClientNavigation(anchor, event)) return;

      const url = new URL(anchor.href, window.location.href);
      const nextPath = `${url.pathname}${url.search}${url.hash}`;
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextPath === currentPath) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      void router.push(nextPath);
    };

    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [router, page.contentHtml]);

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root || permissions.isLoading) return;
    syncGuestAuthLinkVisibility(root, permissions.isAuthenticated);
  }, [permissions.isLoading, permissions.isAuthenticated, page.contentHtml]);

  React.useEffect(() => {
    const rootEl = document.documentElement;
    const applied: Array<{ name: string; previous: string | null }> = [];

    Object.entries(page.htmlAttributes || {}).forEach(([name, value]) => {
      if (!value) return;
      applied.push({ name, previous: rootEl.getAttribute(name) });
      rootEl.setAttribute(name, value);
    });

    if (!rootEl.classList.contains('w-mod-js')) {
      rootEl.classList.add('w-mod-js');
      applied.push({ name: 'class:w-mod-js', previous: null });
    }

    return () => {
      applied.forEach(({ name, previous }) => {
        if (name === 'class:w-mod-js') {
          // Leave w-mod-js — other Webflow pages may still need it.
          return;
        }
        if (previous == null) {
          rootEl.removeAttribute(name);
        } else {
          rootEl.setAttribute(name, previous);
        }
      });
    };
  }, [page.htmlAttributes]);

  React.useEffect(() => {
    const scripts = page.assets.scripts ?? [];
    if (scripts.length === 0) return;

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    void injectWebflowScripts(scripts).then((dispose) => {
      if (cancelled) {
        dispose();
        return;
      }
      cleanup = dispose;
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [page.contentHtml, page.assets.scripts]);

  return (
    <>
      <Head>
        {page.title && <title>{page.title}</title>}
        {page.assets.fontLinks.map((link) => (
          <link
            key={`${link.rel}-${link.href}`}
            rel={link.rel}
            href={link.href}
            {...(link.crossOrigin
              ? { crossOrigin: link.crossOrigin as 'anonymous' }
              : {})}
          />
        ))}
        {page.assets.stylesheetHrefs.map((href) => (
          <link key={href} rel="stylesheet" href={href} />
        ))}
      </Head>

      <style
        data-webflow-isolation="chakra-undo"
        dangerouslySetInnerHTML={{ __html: WEBFLOW_CHAKRA_ISOLATION_CSS }}
      />

      {page.assets.inlineStyles.map((css, index) => (
        <style
          key={`webflow-style-${index}`}
          data-webflow-isolation="scoped-css"
          dangerouslySetInnerHTML={{ __html: css }}
        />
      ))}

      <style
        data-webflow-isolation="hover-align"
        dangerouslySetInnerHTML={{ __html: WEBFLOW_HOVER_ALIGN_CSS }}
      />

      {hideWebflowNav ? (
        <style
          data-webflow-isolation="hide-webflow-nav"
          dangerouslySetInnerHTML={{ __html: WEBFLOW_NAV_HIDE_CSS }}
        />
      ) : null}

      <div
        ref={rootRef}
        className={wrapperClassName}
        data-webflow-content-root=""
        dangerouslySetInnerHTML={{ __html: page.contentHtml }}
      />

      {/* Static Webflow chrome only: avatar overlays nav without reflowing Webflow layout. */}
      {!hideWebflowNav ? (
        <WebflowStaticNavAccountPortal
          containerRef={rootRef}
          htmlKey={page.contentHtml}
        />
      ) : null}
    </>
  );
};
